// ReceiptModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Alert } from 'react-bootstrap';
import AutocompleteSelect from '../../../../cabinet/wizplain/components/Autocomplete/AutocompleteSelect';
import kitchenStockApi from './kitchenStockApi';
import useProductAutocomplete from '../../../../../components/ProductAutocomplete/useProductAutocomplete';

const emptyForm = {
  productId: '',
  quantityReceived: '',
  price: '',
  supplier: '',
  documentNumber: '',
  documentDate: '',
  receivedDate: new Date().toISOString().slice(0, 10),
  expiryDate: '',
  addInfo: ''
};

const ReceiptModal = ({ isOpen, onClose, onSubmit }) => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddName, setQuickAddName] = useState('');
  const [quickAddBaseId, setQuickAddBaseId] = useState('');
  const [quickAddPackageSize, setQuickAddPackageSize] = useState('');
  const [quickAddSubmitting, setQuickAddSubmitting] = useState(false);
  const [quickAddError, setQuickAddError] = useState(null);

  // What did we actually receive — searches the trade-items dictionary (product-items),
  // not base products (see reference.split-products.patch.md).
  const productSearch = useProductAutocomplete({ referenceType: 'product-items' });
  // Base products only, for the quick-add "which base product does this brand belong to" picker.
  const baseProductSearch = useProductAutocomplete({ referenceType: 'products' });

  useEffect(() => {
    if (!isOpen) return;
    setForm(emptyForm);
    setError(null);
    setShowQuickAdd(false);
    setQuickAddName('');
    setQuickAddBaseId('');
    setQuickAddPackageSize('');
    setQuickAddError(null);
    productSearch.loadInitial();
    baseProductSearch.loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleQuickAddSubmit = async () => {
    setQuickAddError(null);

    if (!quickAddName || !quickAddBaseId) {
      setQuickAddError('Вкажіть торгову назву і оберіть базовий продукт');
      return;
    }

    setQuickAddSubmitting(true);
    try {
      const created = await kitchenStockApi.createTradeProduct({
        name: quickAddName,
        baseProductId: quickAddBaseId,
        packageSize: quickAddPackageSize ? Number(quickAddPackageSize) : undefined
      });
      const newId = created && (created._id || created.id);
      // Pull the new item back into the main picker's option list (search by its own name) and
      // select it right away.
      await productSearch.search(quickAddName);
      if (newId) {
        setForm(prev => ({ ...prev, productId: newId }));
      }
      setShowQuickAdd(false);
      setQuickAddName('');
      setQuickAddBaseId('');
      setQuickAddPackageSize('');
    } catch (err) {
      setQuickAddError(err.message);
    } finally {
      setQuickAddSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.productId || !form.quantityReceived || !form.receivedDate) {
      setError('Заповніть продукт, кількість і дату надходження');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        ...form,
        quantityReceived: Number(form.quantityReceived),
        price: form.price ? Number(form.price) : undefined
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Оприбуткувати продукцію</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

          <AutocompleteSelect
            label="Торгова позиція *"
            value={form.productId}
            onChange={(id) => setForm(prev => ({ ...prev, productId: id }))}
            options={productSearch.options}
            loading={productSearch.loading}
            loadingMore={productSearch.loadingMore}
            hasMore={productSearch.hasMore}
            onSearch={productSearch.search}
            onLoadMore={productSearch.loadMore}
            placeholder="Почніть вводити торгову назву..."
          />
          <Button
            variant="link"
            size="sm"
            className="px-0 mt-1 mb-3"
            onClick={() => setShowQuickAdd(prev => !prev)}
          >
            {showQuickAdd ? 'Сховати' : 'Не знайшли потрібну торгову назву? Додати нову'}
          </Button>

          {showQuickAdd && (
            <div className="border rounded p-3 mb-3 bg-light">
              {quickAddError && <Alert variant="danger" className="py-2 mb-2">{quickAddError}</Alert>}
              <Form.Group className="mb-2">
                <Form.Label className="small mb-1">Торгова назва (з упаковкою)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="напр. Хуторянка масло вершкове 82,5% 300г"
                  value={quickAddName}
                  onChange={(e) => setQuickAddName(e.target.value)}
                />
              </Form.Group>
              <div className="mb-2">
                <AutocompleteSelect
                  label="Базовий продукт *"
                  value={quickAddBaseId}
                  onChange={setQuickAddBaseId}
                  options={baseProductSearch.options}
                  loading={baseProductSearch.loading}
                  loadingMore={baseProductSearch.loadingMore}
                  hasMore={baseProductSearch.hasMore}
                  onSearch={baseProductSearch.search}
                  onLoadMore={baseProductSearch.loadMore}
                  placeholder="Почніть вводити назву базового продукту..."
                />
                <Form.Text className="text-muted">
                  Одиниця виміру, КБЖУ й алергени візьмуться з базового продукту автоматично.
                  Немає потрібного базового продукту — його спершу додає адміністратор у
                  довіднику базових продуктів.
                </Form.Text>
              </div>
              <Form.Group className="mb-2">
                <Form.Label className="small mb-1">
                  Вага/об'єм однієї упаковки (необовʼязково, у базовій одиниці виміру)
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.001"
                  min="0"
                  placeholder="напр. 200 (якщо базовий продукт обліковується в грамах)"
                  value={quickAddPackageSize}
                  onChange={(e) => setQuickAddPackageSize(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Дозволить бачити залишок цієї позиції ще й у штуках.
                </Form.Text>
              </Form.Group>
              <Button
                size="sm"
                variant="outline-primary"
                type="button"
                disabled={quickAddSubmitting}
                onClick={handleQuickAddSubmit}
              >
                {quickAddSubmitting && <Spinner animation="border" size="sm" className="me-2" />}
                Додати до довідника
              </Button>
            </div>
          )}

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Кількість *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.001"
                  min="0"
                  name="quantityReceived"
                  value={form.quantityReceived}
                  onChange={handleChange}
                  required
                />
                <Form.Text className="text-muted">
                  У базовій одиниці виміру продукту (кг, г, л...).
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Ціна за одиницю</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  placeholder="грн"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Постачальник</Form.Label>
                <Form.Control
                  type="text"
                  name="supplier"
                  value={form.supplier}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>№ накладної</Form.Label>
                <Form.Control
                  type="text"
                  name="documentNumber"
                  value={form.documentNumber}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Дата документа</Form.Label>
                <Form.Control
                  type="date"
                  name="documentDate"
                  value={form.documentDate}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Дата надходження *</Form.Label>
                <Form.Control
                  type="date"
                  name="receivedDate"
                  value={form.receivedDate}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Термін придатності до</Form.Label>
                <Form.Control
                  type="date"
                  name="expiryDate"
                  value={form.expiryDate}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Примітка</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="addInfo"
              value={form.addInfo}
              onChange={handleChange}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Скасувати</Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting && <Spinner animation="border" size="sm" className="me-2" />}
            Оприбуткувати
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ReceiptModal;